import React, { useState, useMemo, useEffect } from 'react';
import { MapPin, Users, Calendar, Clock, Repeat, Star, Home, Phone, Mail, Globe } from 'lucide-react';
import { apiService } from '../../services/apiService';
import { toast } from 'sonner';
import RunCard from './RunCard';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './dialog';
import { convertHeicToWebFormat, getAllImageUrls } from '../../utils/imageUtils';
import { Carousel, CarouselContent, CarouselItem } from './carousel';
import { formatDate, formatTime } from '../../utils/dataTransformers';
import { convertUTCToMeetingTimezone } from '../../utils/dateTimeUtils';

const ScrollableMeetingCard = ({ 
  meeting,
  event, // Support event prop as alias
  onClick, 
  showHover = true,
  showTimeUntil = true,
  showDifficulty = true,
  showAttendees = true,
  showLocation = true,
  showDate = true,
  buttonText = "View Run",
  onButtonClick,
  isFullProfile = false,
  onMeetingUpdate
}) => {
  const [showParticipants, setShowParticipants] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [showRunCard, setShowRunCard] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [loadingRunCard, setLoadingRunCard] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [meetingParticipants, setMeetingParticipants] = useState([]);
  const [loadingMeetingParticipants, setLoadingMeetingParticipants] = useState(false);
  const [clubInfo, setClubInfo] = useState(null);
  const [loadingClubInfo, setLoadingClubInfo] = useState(false);

  // Use meeting or event (event is alias for compatibility)
  const eventData = meeting || event;

  // Fetch participants and club info when component mounts or eventData changes
  useEffect(() => {
    if (eventData?.club_id && eventData?.id) {
      // Fetch participants
      setLoadingMeetingParticipants(true);
      apiService.clubs.getRunParticipants(eventData.club_id, eventData.id)
        .then(response => {
          setMeetingParticipants(response || []);
        })
        .catch(error => {
          console.error('Failed to fetch participants:', error);
          setMeetingParticipants([]);
        })
        .finally(() => {
          setLoadingMeetingParticipants(false);
        });

      // Fetch club info
      setLoadingClubInfo(true);
      apiService.clubs.getProfile(eventData.club_id)
        .then(response => {
          setClubInfo(response?.club || null);
        })
        .catch(error => {
          console.error('Failed to fetch club info:', error);
          setClubInfo(null);
        })
        .finally(() => {
          setLoadingClubInfo(false);
        });
    }
  }, [eventData?.club_id, eventData?.id]);

  // Safety check
  if (!eventData) {
    return null;
  }

  // Constants
  const DESCRIPTION_CHAR_LIMIT = 150;
  const DEFAULT_DESCRIPTION = '';

  // Helper function to get initials
  const getInitial = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  // Get all image URLs from images array, or fall back to single image
  const imageUrls = useMemo(() => {
    if (eventData.images && Array.isArray(eventData.images) && eventData.images.length > 0) {
      return getAllImageUrls(eventData.images);
    }
    if (eventData.image) {
      return [convertHeicToWebFormat(eventData.image)];
    }
    return [];
  }, [eventData.images, eventData.image]);

  // Ensure image URL is converted from HEIC to JPG for web compatibility
  const displayImageUrl = useMemo(() => {
    if (imageUrls.length > 0) {
      return imageUrls[0];
    }
    return null;
  }, [imageUrls]);

  // Memoized description logic with error handling
  const descriptionLogic = useMemo(() => {
    try {
      const description = eventData?.description || DEFAULT_DESCRIPTION;
      const shouldTruncate = description.length > DESCRIPTION_CHAR_LIMIT;
      const displayText = shouldTruncate && !isDescriptionExpanded 
        ? description.substring(0, DESCRIPTION_CHAR_LIMIT) + '...'
        : description;
      
      return {
        description,
        shouldTruncate,
        displayText
      };
    } catch (error) {
      console.error('Error processing description:', error);
      return {
        description: DEFAULT_DESCRIPTION,
        shouldTruncate: false,
        displayText: DEFAULT_DESCRIPTION
      };
    }
  }, [eventData?.description, isDescriptionExpanded]);

  const handleClick = () => {
    if (onClick) {
      onClick(eventData);
    }
  };

  const handleButtonClick = (e) => {
    e.stopPropagation();
    if (onButtonClick) {
      onButtonClick(eventData);
    }
  };

  const handleScroll = (e) => {
    const scrollTop = e.target.scrollTop;
    setIsScrolled(scrollTop > 20);
  };

  const handleAttendeesClick = async (e) => {
    e.stopPropagation();
    
    if (!eventData.club_id || !eventData.id) {
      toast.error('Meeting information not available');
      return;
    }
    
    const meetingId = eventData.id;
    
    setShowParticipants(true);
    setLoadingParticipants(true);
    
    try {
      const response = await apiService.clubs.getRunParticipants(eventData.club_id, meetingId);
      setParticipants(response);
    } catch (error) {
      console.error('Failed to fetch participants:', error);
      toast.error('Failed to load participants');
      setParticipants([]);
    } finally {
      setLoadingParticipants(false);
    }
  };

  const handleParticipantClick = async (participant) => {
    if (participant.user_id) {
      setLoadingRunCard(true);
      setSelectedParticipant(participant);
      setShowRunCard(true);
      
      try {
        const userProfile = await apiService.users.getProfile(participant.user_id);
        setSelectedParticipant(userProfile);
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
        toast.error('Failed to load run card');
        setShowRunCard(false);
      } finally {
        setLoadingRunCard(false);
      }
    } else {
      console.error('No user_id found in participant data');
      toast.error('Unable to view participant profile');
    }
  };

  const handleBackToParticipants = () => {
    setShowRunCard(false);
    setSelectedParticipant(null);
  };

  return (
    <>
      <div
        className={`rounded-3xl overflow-hidden shadow-2xl border border-white/20 relative w-[400px] bg-runcards-background ${
          showHover ? 'hover:scale-105 transition-all duration-300 hover:shadow-3xl' : ''
        } ${onClick ? 'cursor-pointer' : ''}`}
        onClick={handleClick}
      >
        {/* Scrollable Content */}
        <div 
          className={`${isFullProfile ? 'max-h-[80vh] overflow-y-auto scrollbar-hide' : ''}`}
          onScroll={handleScroll}
        >
          {/* Image Section */}
          <div className="relative h-[500px] overflow-hidden rounded-t-3xl">
            {imageUrls.length > 1 ? (
              // Multiple images - use carousel
              <Carousel
                opts={{
                  align: "start",
                  loop: true,
                }}
                className="w-full h-full"
                setApi={(api) => {
                  if (api) {
                    // Set initial index
                    setCurrentImageIndex(api.selectedScrollSnap());
                    // Listen for changes
                    api.on("select", () => {
                      setCurrentImageIndex(api.selectedScrollSnap());
                    });
                  }
                }}
              >
                <CarouselContent className="h-full -ml-0">
                  {imageUrls.map((url, index) => (
                    <CarouselItem key={index} className="h-full pl-0 basis-full">
                      <div className="relative w-full h-full">
                        <img 
                          src={url} 
                          alt={`${eventData.name} - Image ${index + 1}`}
                          className="w-full h-full object-cover"
                          style={{
                            objectPosition: eventData.imagePosition ? `${eventData.imagePosition.x}% ${eventData.imagePosition.y}%` : 'center center'
                          }}
                          onError={(e) => {
                            console.error('Failed to load event image:', url);
                            setImageError(true);
                            e.target.style.display = 'none';
                          }}
                          onLoad={() => {
                            setImageError(false);
                          }}
                        />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            ) : displayImageUrl && !imageError ? (
              // Single image
              <img 
                src={imageSrc || displayImageUrl} 
                alt={eventData.name}
                className="w-full h-full object-cover"
                style={{
                  objectPosition: eventData.imagePosition ? `${eventData.imagePosition.x}% ${eventData.imagePosition.y}%` : 'center center'
                }}
                onError={(e) => {
                  const currentSrc = e.target.src;
                  if (!imageSrc && eventData.image && eventData.image.includes('.heic')) {
                    const convertedUrl = convertHeicToWebFormat(eventData.image);
                    if (convertedUrl !== currentSrc) {
                      setImageSrc(convertedUrl);
                      return;
                    }
                  }
                  if (!currentSrc.includes('.jpg') || !eventData.image?.includes('.heic')) {
                    console.error('Failed to load event image:', currentSrc);
                  }
                  setImageError(true);
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
                onLoad={() => {
                  setImageError(false);
                }}
              />
            ) : null}
            <div className={`w-full h-full bg-runcards-background ${displayImageUrl && !imageError ? 'spray-paint-placeholder' : 'spray-paint-placeholder'}`} style={{display: displayImageUrl && !imageError ? 'none' : 'flex'}}>
            </div>
            
            {/* Time Until Badge - Top Left */}
            {showTimeUntil && eventData.timeUntil && (
              <div className="absolute top-4 left-4">
                <span className="bg-black/60 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-medium border border-white/20">
                  {eventData.timeUntil}
                </span>
              </div>
            )}

            {/* Difficulty Badge - Top Right */}
            {showDifficulty && eventData.difficulty && 
             eventData.difficulty.toLowerCase() !== 'intermediate' && 
             eventData.difficulty.toLowerCase() !== 'advanced' && (
              <div className="absolute top-4 right-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium border border-white/20 ${
                  eventData.difficulty === 'Beginner' ? 'bg-green-600/60 backdrop-blur-sm text-white' :
                  'bg-red-600/60 backdrop-blur-sm text-white'
                }`}>
                  {eventData.difficulty}
                </span>
              </div>
            )}

            {/* Recurring Badge - Top Right (below difficulty) */}
            {eventData.is_recurring && eventData.recurrence_pattern && eventData.recurrence_pattern.toLowerCase() !== 'weekly' && (
              <div className="absolute top-16 right-4">
                <span className="bg-blue-600/60 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-medium border border-white/20 flex items-center gap-1">
                  <Repeat className="w-3 h-3" />
                  {eventData.recurrence_pattern === 'daily' ? 'Daily' : 
                   eventData.recurrence_pattern}
                </span>
              </div>
            )}

            {/* Dots Indicator for multiple images */}
            {imageUrls.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
                {imageUrls.map((_, index) => (
                  <button
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentImageIndex 
                        ? 'bg-white w-6' 
                        : 'bg-white/50 hover:bg-white/75'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Information Section - Dark background like mobile */}
          <div className="bg-runcards-background rounded-b-3xl">
            <div className="p-6 space-y-6">
              {/* Title and Event Type Badge */}
              <div className="flex items-start justify-between gap-4">
                <h3 className="text-2xl font-bold text-runcards-text-primary font-runcards-bold flex-1">
                  {eventData.name}
                </h3>
                {eventData.event_type && (
                  <span className="bg-runcards-accent/20 text-runcards-accent px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap">
                    {eventData.event_type === 'social' ? 'Social' : 
                     eventData.event_type === 'performance' ? 'Performance' : 
                     eventData.event_type === 'training' ? 'Training' :
                     eventData.event_type}
                  </span>
                )}
              </div>

              {/* Quick Details Row */}
              <div className="flex flex-wrap gap-3 text-sm text-runcards-text-secondary">
                {eventData.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{eventData.location}</span>
                  </div>
                )}
                {eventData.scheduled_date && (
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{formatTime(eventData.scheduled_date, eventData.timezone || null)}</span>
                  </div>
                )}
                {eventData.distance && (
                  <div className="flex items-center gap-1">
                    <span>{eventData.distance}</span>
                  </div>
                )}
                {eventData.difficulty && (
                  <div className="flex items-center gap-1">
                    <span>Level: {eventData.difficulty}</span>
                  </div>
                )}
              </div>

              {/* Action Button */}
              {onButtonClick && (
                <div className="flex justify-center">
                  <button 
                    className="bg-runcards-accent text-runcards-white px-8 py-3 rounded-full font-semibold text-base hover:bg-runcards-accent-hover transition-colors shadow-lg focus:outline-none focus:ring-2 focus:ring-runcards-accent w-full max-w-xs" 
                    onClick={handleButtonClick}
                  >
                    {buttonText}
                  </button>
                </div>
              )}

              {/* Led by Section */}
              {(clubInfo || eventData.club_name || eventData.club_owner_name) && (
                <div className="flex items-center gap-3 p-4 bg-runcards-foreground rounded-xl border border-runcards-border">
                  {clubInfo?.profile_picture || eventData.club_profile_picture ? (
                    <img 
                      src={clubInfo?.profile_picture || eventData.club_profile_picture} 
                      alt={clubInfo?.name || eventData.club_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-runcards-accent flex items-center justify-center">
                      <span className="text-runcards-white font-bold text-lg">
                        {(clubInfo?.name || eventData.club_name || 'U').charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="text-runcards-text-secondary text-sm">
                      Led by {clubInfo?.organizer_name || eventData.club_owner_name || 'Organizer'}
                    </p>
                    {(clubInfo?.name || eventData.club_name) && (
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-runcards-text-primary font-semibold">
                          {clubInfo?.name || eventData.club_name}
                        </p>
                        {clubInfo?.rating && clubInfo?.total_ratings > 0 && (
                          <div className="flex items-center gap-1">
                            <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                            <span className="text-runcards-text-secondary text-xs">
                              {clubInfo.rating.toFixed(1)} ({clubInfo.total_ratings} {clubInfo.total_ratings === 1 ? 'review' : 'reviews'})
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* About This Run Section */}
              {descriptionLogic.displayText ? (
                <div>
                  <h4 className="text-lg font-bold text-runcards-text-primary mb-3 font-runcards-bold">
                    About This Run
                  </h4>
                  <p className="text-runcards-text-secondary text-sm leading-relaxed">
                    {descriptionLogic.displayText}
                    {descriptionLogic.shouldTruncate && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsDescriptionExpanded(!isDescriptionExpanded);
                        }}
                        className="text-runcards-accent ml-1 font-semibold hover:text-runcards-accent-hover transition-colors"
                      >
                        {isDescriptionExpanded ? ' Show Less' : ' Show More'}
                      </button>
                    )}
                  </p>
                </div>
              ) : null}

              {/* Who's Running Section */}
              <div>
                <h4 className="text-lg font-bold text-runcards-text-primary mb-3 font-runcards-bold">
                  Who's Running ({meetingParticipants.length})
                </h4>
                {loadingMeetingParticipants ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="text-runcards-text-secondary text-sm">Loading participants...</div>
                  </div>
                ) : meetingParticipants.length > 0 ? (
                  <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                    {meetingParticipants.map((participant, index) => (
                      <div
                        key={participant.user_id || index}
                        className="flex flex-col items-center gap-2 min-w-[80px] cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleParticipantClick(participant);
                        }}
                      >
                        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-runcards-accent">
                          {participant.user_profile_picture || participant.profile_picture ? (
                            <img 
                              src={participant.user_profile_picture || participant.profile_picture} 
                              alt={participant.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-runcards-accent flex items-center justify-center">
                              <span className="text-runcards-white font-bold">
                                {(participant.name || 'A').charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                        <p className="text-runcards-text-primary text-xs text-center font-medium max-w-[80px] truncate">
                          {participant.name || 'Anonymous'}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-runcards-text-secondary text-sm">No participants yet</p>
                )}
              </div>

              {/* Run Details and Address Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Run Details */}
                <div>
                  <h4 className="text-lg font-bold text-runcards-text-primary mb-3 font-runcards-bold">
                    Run Details
                  </h4>
                  <ul className="space-y-2 text-sm text-runcards-text-secondary">
                    {eventData.scheduled_date && (
                      <li className="flex items-start gap-2">
                        <span className="text-runcards-accent mt-1">•</span>
                        <span>
                          Date & Time: {formatDate(eventData.scheduled_date, { format: 'short', timezone: eventData.timezone || null })} at {formatTime(eventData.scheduled_date, eventData.timezone || null)}
                        </span>
                      </li>
                    )}
                    {eventData.distance && (
                      <li className="flex items-start gap-2">
                        <span className="text-runcards-accent mt-1">•</span>
                        <span>Distance: {eventData.distance}</span>
                      </li>
                    )}
                    {eventData.event_type && (
                      <li className="flex items-start gap-2">
                        <span className="text-runcards-accent mt-1">•</span>
                        <span>Type: {eventData.event_type === 'social' ? 'Social' : eventData.event_type === 'performance' ? 'Performance' : eventData.event_type === 'training' ? 'Training' : eventData.event_type}</span>
                      </li>
                    )}
                    {/* Weather Display */}
                    <li className="flex items-start gap-2">
                      <span className="text-runcards-accent mt-1">•</span>
                      <span>
                        {eventData.weather && eventData.weather.temperature !== undefined && eventData.weather.temperature !== null
                          ? `Weather: ${Math.round(eventData.weather.temperature)}°F, ${eventData.weather.condition || eventData.weather.description || 'Unknown'}`
                          : 'Weather: Available 24 hours before'}
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Address Section */}
                {eventData.address && (
                  <div>
                    <h4 className="text-lg font-bold text-runcards-text-primary mb-3 font-runcards-bold">
                      Address
                    </h4>
                    <div className="flex items-start gap-2 p-3 bg-runcards-foreground rounded-lg border border-runcards-border">
                      <Home className="w-4 h-4 text-runcards-text-secondary mt-0.5 flex-shrink-0" />
                      <span className="text-runcards-text-secondary text-sm flex-1">{eventData.address}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Participants Modal */}
      <Dialog open={showParticipants} onOpenChange={(open) => {
        setShowParticipants(open);
        if (!open) {
          setShowRunCard(false);
        }
      }}>
        <DialogContent className="bg-black/90 backdrop-blur-xl border border-white/20 max-w-md mx-auto shadow-2xl">
          {!showRunCard ? (
            <>
              <DialogHeader className="pb-4">
                <DialogTitle className="text-white flex items-center text-xl font-bold" style={{fontFamily: 'Arial Black, Arial, sans-serif'}}>
                  <Users className="w-6 h-6 mr-3" />
                  Participants ({participants.length})
                </DialogTitle>
                <DialogDescription className="text-white/70 text-sm mt-1" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  People registered for this run
                </DialogDescription>
              </DialogHeader>
              
              <div className="max-h-80 overflow-y-auto">
                {loadingParticipants ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-white/60" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      Loading participants...
                    </div>
                  </div>
                ) : participants.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-white/60" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      No participants yet
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {participants.map((participant, index) => {
                      return (
                      <div key={participant.user_id || index} className="flex items-center space-x-4 p-5 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                        <div className="w-16 h-16 bg-white/10 border border-white/20 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {participant.user_profile_picture ? (
                            <img 
                              src={participant.user_profile_picture} 
                              alt={participant.name}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            <Users className="w-8 h-8 text-white/60" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <button
                            onClick={() => handleParticipantClick(participant)}
                            className="text-white font-semibold hover:text-blue-300 transition-colors duration-200 text-left w-full text-base"
                            style={{fontFamily: 'Montserrat, sans-serif'}}
                          >
                            {participant.name || 'Unknown User'}
                          </button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <DialogHeader className="sr-only">
                <DialogTitle>Run Card</DialogTitle>
                <DialogDescription>
                  {selectedParticipant?.name ? `Run card for ${selectedParticipant.name}` : 'Loading run card'}
                </DialogDescription>
              </DialogHeader>
              
              <div className="relative">
                <div className="flex justify-center">
                  {loadingRunCard ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-white/60" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        Loading run card...
                      </div>
                    </div>
                  ) : selectedParticipant ? (
                    <RunCard 
                      runner={selectedParticipant}
                      showHover={false}
                      buttonText="View Profile"
                      onButtonClick={() => {
                        toast.info('Profile details coming soon');
                      }}
                    />
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-white/60" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        Unable to load run card
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ScrollableMeetingCard;

